/**
 * TASK DETAIL MODAL - Linear-style task editor with tabs
 *
 * General tab: edit title, description, pipeline progress, settings.
 * Logs tab: live job status, workflow run details, step output.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useWorldStore, type Task, type Priority, type Complexity, type Impact } from "@/stores/worldStore";

// Error boundary to prevent full-page crashes
class ModalErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="p-6 text-center">
          <p className="text-red-400 font-medium mb-2">Component Error</p>
          <pre className="text-xs text-zinc-400 bg-zinc-900 rounded p-3 text-left whitespace-pre-wrap max-h-48 overflow-auto">
            {this.state.error.message}
            {"\n"}
            {this.state.error.stack}
          </pre>
          <button
            onClick={() => this.setState({ error: null })}
            className="mt-3 px-3 py-1.5 text-sm bg-zinc-700 hover:bg-zinc-600 rounded text-white"
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Trash2,
  Save,
  CheckCircle2,
  Circle,
  Loader2,
  XCircle,
  RefreshCw,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Play,
  Clock,
  Square,
  Terminal,
  FileText,
  AlertTriangle,
  Copy,
  Check,
  ChevronsUpDown,
  GitMerge,
  SkipForward,
  PauseCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useWorkspaceFeatures } from "@/hooks/useWorkspaceFeatures";
import { useWorkerStore } from "@/stores/workerStore";
import { useWorkflowStore } from "@/stores/workflowStore";
import { WorkflowTimeline } from "../workflow/WorkflowTimeline";
import { getMergeErrorMessage, parseMergeError } from "@/lib/mergeErrors";
import { cn, truncateId } from "@/lib/utils";
import { api } from "@/lib/api";

interface TaskDetailModalProps {
  task: Task | null;
  isOpen: boolean;
  onClose: () => void;
}

const issueTypeToCategory: Record<string, string> = {
    "wp": "Refactor",
    "bug": "Bug Report",
    "feature": "Feature Request",
    "hotfix": "Hotfix"
};

const BUGFIX_PIPELINE_STEPS = [
  { key: "diagnose", label: "Diagnose", description: "Analyze issue and find root cause" },
  { key: "fix", label: "Fix", description: "Create branch and apply fix" },
  { key: "verify", label: "Verify", description: "Run test suite" },
  { key: "review", label: "Review", description: "AI code review" },
  { key: "pr", label: "Pull Request", description: "Push and open draft PR" },
];

function humanizeStepKey(key: string): string {
  return key
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatElapsed(ms: number): string {
  if (ms < 0) return "";
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remaining}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function getStepDuration(startedAt?: string, completedAt?: string): string | null {
  if (!startedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const ms = end - start;
  return ms > 0 ? formatElapsed(ms) : null;
}

function timeAgo(isoString: string): string {
  const ms = Date.now() - new Date(isoString).getTime();
  if (ms < 60000) return "just now";
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}h ago`;
  return `${Math.floor(ms / 86400000)}d ago`;
}

interface PipelineStep {
  key: string;
  label: string;
  description: string;
  status?: string;
  started_at?: string;
  completed_at?: string;
  error?: string;
  logs?: string;
  output?: string;
  artifacts?: Record<string, any>;
}

function buildPipelineSteps(metadata?: Record<string, any>): PipelineStep[] {
  if (!metadata) return [];

  const bugfixSteps = metadata.bugfix_steps;
  const workflowSteps = metadata.workflow_steps;

  if (bugfixSteps && typeof bugfixSteps === "object" && Object.keys(bugfixSteps).length > 0) {
    return BUGFIX_PIPELINE_STEPS.map((step) => {
      const data = bugfixSteps[step.key];
      return {
        ...step,
        status: data?.status,
        started_at: data?.started_at,
        completed_at: data?.completed_at,
        error: data?.error,
        logs: data?.logs,
        artifacts: data?.artifacts,
      };
    });
  }

  if (workflowSteps && typeof workflowSteps === "object" && Object.keys(workflowSteps).length > 0) {
    return Object.entries(workflowSteps).map(([key, value]: [string, any]) => ({
      key,
      label: humanizeStepKey(key),
      description: "",
      status: value?.status,
      started_at: value?.started_at,
      completed_at: value?.completed_at,
      error: value?.error,
      logs: value?.logs,
      output: value?.output,
      artifacts: value?.artifacts,
    }));
  }

  return [];
}

function StepIcon({ status }: { status?: string }) {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    case "running":
      return <Loader2 className="w-4 h-4 animate-spin text-violet-400" />;
    case "failed":
      return <XCircle className="w-4 h-4 text-red-500" />;
    default:
      return <Circle className="w-4 h-4 text-zinc-600" />;
  }
}

// ── Job status badge ──────────────────────────────────────────────
function JobStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    queued: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    processing: "bg-violet-500/10 text-violet-400 border-violet-500/20",
    running: "bg-violet-500/10 text-violet-400 border-violet-500/20",
    completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    failed: "bg-red-500/10 text-red-400 border-red-500/20",
    cancelled: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
  };
  return (
    <span className={cn(
      "text-xs px-2 py-0.5 rounded-full border font-medium",
      styles[status] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"
    )}>
      {status}
    </span>
  );
}

// ── Types for log data ────────────────────────────────────────────
interface JobLogEntry {
  id: number | string;
  event_type: string;
  status: string;
  error: string | null;
  created_at: string;
  updated_at: string;
}

interface WorkflowRunLog {
  id: string;
  status: string;
  current_node_id: string | null;
  node_results: Record<string, any>;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
}

interface OutputLogLine {
  id: number;
  node_id: string | null;
  timestamp: string;
  line: string;
}

interface TaskLogsData {
  jobs: JobLogEntry[];
  workflow_run: WorkflowRunLog | null;
  output_lines: OutputLogLine[];
}

function mapLocalJobStatus(status: string): string {
  switch (status) {
    case "queued":
      return "pending";
    case "running":
      return "processing";
    default:
      return status;
  }
}

// ── Failed Workflow Panel ─────────────────────────────────────────
function FailedWorkflowPanel({
  workflowRun,
  nodeResults,
  taskId,
}: {
  workflowRun: { id: string; status: string; current_node_id: string | null; error: string | null };
  nodeResults: Record<string, any>;
  taskId: string;
}) {
  const { retryTaskPipeline, resumeTaskFromFailedNode } = useWorldStore();
  const { canRetryPipeline } = useWorkspaceFeatures();
  const { toast } = useToast();

  // Find the failed node
  const failedNodeId = Object.keys(nodeResults).find(
    (nid) => nodeResults[nid]?.status === "failed"
  ) || workflowRun.current_node_id;
  const failedResult = failedNodeId ? nodeResults[failedNodeId] : null;
  const failedLabel = failedNodeId || "unknown";

  const handleResume = async () => {
    const success = await resumeTaskFromFailedNode(taskId);
    if (success) {
      toast({ title: `Resuming from ${failedLabel}` });
    } else {
      toast({ title: "Failed to resume pipeline", variant: "destructive" });
    }
  };

  const handleRetry = async () => {
    const success = await retryTaskPipeline(taskId);
    if (success) {
      toast({ title: "Pipeline retry queued" });
    } else {
      toast({ title: "Failed to retry pipeline", variant: "destructive" });
    }
  };

  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 mb-3 space-y-2">
      <div className="text-sm font-medium text-red-300 flex items-center gap-2">
        <XCircle className="w-4 h-4" />
        Workflow failed{failedNodeId ? ` at: ${failedLabel}` : ""}
      </div>
      {failedResult?.error && (
        <p className="text-xs text-red-400/80">{failedResult.error}</p>
      )}
      {workflowRun.error && !failedResult?.error && (
        <p className="text-xs text-red-400/80">{workflowRun.error}</p>
      )}
      <div className="flex gap-2 pt-1">
        {failedNodeId && (
          <Button size="sm" onClick={handleResume} className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-500">
            <SkipForward className="w-3 h-3" />
            Resume from {failedLabel}
          </Button>
        )}
        {canRetryPipeline && (
          <Button size="sm" variant="outline" onClick={handleRetry} className="h-7 text-xs gap-1">
            <RefreshCw className="w-3 h-3" />
            Retry from start
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Review Exhausted Banner ───────────────────────────────────────
function ReviewExhaustedBanner({
  taskId,
  nodeResults,
  reviewNodeId,
}: {
  taskId: string;
  nodeResults: Record<string, any>;
  reviewNodeId: string;
}) {
  const { resumeTaskFromFailedNode } = useWorldStore();
  const { toast } = useToast();
  const [feedback, setFeedback] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);

  const reviewData = nodeResults[reviewNodeId] || {};
  const targetNodeId = reviewData.review_target_node_id;
  const lastSummary = reviewData.review_history?.slice(-1)?.[0]?.summary || "Review found issues.";

  const handleApprove = async () => {
    const success = await resumeTaskFromFailedNode(taskId, "approve");
    if (success) toast({ title: "Task resumed — accepted with warnings" });
    else toast({ title: "Failed to resume", variant: "destructive" });
  };

  const handleReject = async () => {
    const success = await resumeTaskFromFailedNode(taskId, "reject_to", targetNodeId, feedback || undefined);
    if (success) toast({ title: `Retrying from ${targetNodeId}` });
    else toast({ title: "Failed to retry", variant: "destructive" });
  };

  const handleStop = async () => {
    const success = await resumeTaskFromFailedNode(taskId, "stop");
    if (success) toast({ title: "Task stopped" });
    else toast({ title: "Failed to stop", variant: "destructive" });
  };

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 mb-3 space-y-2">
      <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
        <AlertTriangle className="w-4 h-4" />
        AI Review needs your input
      </div>
      <p className="text-xs text-zinc-400">{lastSummary}</p>
      {showFeedback && (
        <textarea
          className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-sm text-zinc-300 mt-2"
          placeholder="Add instructions for the retry..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={3}
        />
      )}
      <div className="flex gap-2 mt-2">
        <button onClick={handleApprove} className="px-3 py-1 text-xs rounded bg-emerald-600 hover:bg-emerald-500 text-white">
          Accept anyway
        </button>
        <button
          onClick={() => showFeedback ? handleReject() : setShowFeedback(true)}
          className="px-3 py-1 text-xs rounded bg-amber-600 hover:bg-amber-500 text-white"
        >
          {showFeedback ? "Retry with feedback" : "Retry code"}
        </button>
        <button onClick={handleStop} className="px-3 py-1 text-xs rounded bg-red-600 hover:bg-red-500 text-white">
          Stop
        </button>
      </div>
    </div>
  );
}

// ── Logs Tab Component ────────────────────────────────────────────
function LogsTab({ task }: { task: Task }) {
  const [logs, setLogs] = useState<TaskLogsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const logEndRef = useRef<HTMLDivElement>(null);
  const prevWfStatus = useRef<string | undefined>(undefined);
  const { refreshTask } = useWorldStore();
  const { isLocalWorkspace } = useWorkspaceFeatures();
  const workerJobs = useWorkerStore((s) => s.jobs);
  const useLocalLogs = isLocalWorkspace || task.id.startsWith("local:");
  const hasActiveLocalJob = workerJobs.some(
    (job) => job.taskId === task.id && (job.status === "queued" || job.status === "running"),
  );

  const loadLocalLogs = useCallback(async (): Promise<TaskLogsData> => {
    const jobsById = new Map<string, JobLogEntry>();
    let outputLines: OutputLogLine[] = [];
    const memoryJobs = workerJobs
      .filter((job) => job.taskId === task.id)
      .map((job) => ({
        id: job.id,
        event_type: job.eventType ?? "execute_workflow",
        status: mapLocalJobStatus(job.status),
        error: job.error ?? null,
        created_at: job.createdAt,
        updated_at: job.updatedAt,
      }));
    for (const job of memoryJobs) {
      jobsById.set(String(job.id), job);
    }

    let workflowRun: WorkflowRunLog | null = null;

    try {
      const Database = (await import("@tauri-apps/plugin-sql")).default;
      const db = await Database.load("sqlite:factory-local.db");

      const parseNodeResults = (value: unknown): Record<string, any> => {
        if (!value) return {};
        if (typeof value === "string") {
          try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === "object" ? parsed : {};
          } catch {
            return {};
          }
        }
        if (typeof value === "object" && !Array.isArray(value)) {
          return value as Record<string, any>;
        }
        return {};
      };

      let workflowRunRows: any[] = [];
      try {
        workflowRunRows = await db.select(
          `SELECT id, status, current_node_id, node_results, started_at, completed_at, error
           FROM workflow_runs
           WHERE task_id = ?
           ORDER BY started_at DESC
           LIMIT 1`,
          [task.id],
        );
      } catch {
        workflowRunRows = [];
      }

      if (workflowRunRows.length === 0) {
        try {
          workflowRunRows = await db.select(
            `SELECT id, status, current_node_id, node_results, started_at, completed_at, error
             FROM local_workflow_runs
             WHERE task_id = ?
             ORDER BY started_at DESC
             LIMIT 1`,
            [task.id],
          );
        } catch {
          workflowRunRows = [];
        }
      }

      const persistedRun = workflowRunRows[0];
      if (persistedRun) {
        workflowRun = {
          id: String(persistedRun.id),
          status: String(persistedRun.status ?? "running"),
          current_node_id:
            typeof persistedRun.current_node_id === "string" ? persistedRun.current_node_id : null,
          node_results: parseNodeResults(persistedRun.node_results),
          started_at: typeof persistedRun.started_at === "string" ? persistedRun.started_at : null,
          completed_at: typeof persistedRun.completed_at === "string" ? persistedRun.completed_at : null,
          error: typeof persistedRun.error === "string" ? persistedRun.error : null,
        };
      }

      const readPersistedJobs = async (tableName: string): Promise<any[]> => {
        try {
          return await db.select(
            `SELECT id, event_type, status, error, created_at, updated_at
             FROM ${tableName}
             WHERE task_id = ?
             ORDER BY created_at DESC
             LIMIT 25`,
            [task.id],
          );
        } catch {
          return [];
        }
      };

      const persistedJobs = [
        ...(await readPersistedJobs("local_jobs")),
        ...(await readPersistedJobs("jobs")),
      ];

      for (const row of persistedJobs) {
        const entry: JobLogEntry = {
          id: row.id,
          event_type: row.event_type ?? "execute_workflow",
          status: mapLocalJobStatus(String(row.status ?? "pending")),
          error: typeof row.error === "string" ? row.error : null,
          created_at: row.created_at ?? row.updated_at ?? new Date().toISOString(),
          updated_at: row.updated_at ?? row.created_at ?? new Date().toISOString(),
        };
        jobsById.set(String(entry.id), entry);
      }

      // Query output log lines
      try {
        const outputRows: OutputLogLine[] = await db.select(
          `SELECT id, node_id, timestamp, line FROM workflow_output_log
           WHERE task_id = ? ORDER BY id ASC LIMIT 2000`,
          [task.id],
        );
        outputLines = outputRows;
      } catch {
        // Table may not exist yet
      }
    } catch {
      // Fallback to in-memory logs only.
    }

    if (!workflowRun && task.workflowStatus) {
      workflowRun = {
        id: task.workflowRunId ?? `local-run-${task.id}`,
        status: task.workflowStatus,
        current_node_id: null,
        node_results: {},
        started_at: null,
        completed_at: null,
        error: null,
      };
    }

    const jobs = Array.from(jobsById.values()).sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );

    if (jobs.length === 0 && workflowRun) {
      jobs.push({
        id: `wf-${workflowRun.id}`,
        event_type: "execute_workflow",
        status: mapLocalJobStatus(workflowRun.status),
        error: workflowRun.error,
        created_at: workflowRun.started_at ?? new Date().toISOString(),
        updated_at: workflowRun.completed_at ?? workflowRun.started_at ?? new Date().toISOString(),
      });
    }

    return {
      jobs,
      workflow_run: workflowRun,
      output_lines: outputLines,
    };
  }, [task.id, task.workflowRunId, task.workflowStatus, workerJobs]);

  const fetchLogs = useCallback(async () => {
    if (useLocalLogs) {
      const data = await loadLocalLogs();
      setLogs(data);
      setError(null);
      setLoading(false);
      const wfStatus = data.workflow_run?.status;
      if (wfStatus && wfStatus !== prevWfStatus.current) {
        prevWfStatus.current = wfStatus;
        if (wfStatus !== task.workflowStatus) {
          refreshTask(task.id);
        }
      }
      return;
    }

    try {
      const data = await api.get(`/api/tasks/${task.id}/logs`) as TaskLogsData;
      setLogs(data);
      setError(null);

      // If workflow status changed from what the task object has, refresh the task in the store
      const wfStatus = data.workflow_run?.status;
      if (wfStatus && wfStatus !== prevWfStatus.current) {
        prevWfStatus.current = wfStatus;
        if (wfStatus !== task.workflowStatus) {
          refreshTask(task.id);
        }
      }
    } catch (e: any) {
      setError(e.message || "Failed to fetch logs");
    } finally {
      setLoading(false);
    }
  }, [useLocalLogs, loadLocalLogs, task.id, task.workflowStatus, refreshTask]);

  useEffect(() => {
    fetchLogs();
    if (useLocalLogs) {
      const localActive =
        hasActiveLocalJob
        || task.workflowStatus === "running"
        || task.workflowStatus === "queued";
      if (localActive) {
        pollRef.current = setInterval(fetchLogs, 2000);
      }
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }

    // Poll while task has active pipeline (cloud workspace)
    const isActive = task.workflowStatus === "running" || task.workflowStatus === "queued";
    if (isActive) {
      pollRef.current = setInterval(fetchLogs, 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchLogs, hasActiveLocalJob, task.workflowStatus, useLocalLogs]);

  // Auto-scroll to bottom of log on update
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading logs...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 text-red-400 gap-2">
        <AlertTriangle className="w-4 h-4" />
        {error}
      </div>
    );
  }

  const jobs = logs?.jobs || [];
  const workflowRun = logs?.workflow_run;
  const nodeResults = workflowRun?.node_results || {};
  const nodeKeys = Object.keys(nodeResults);

  // Get workflow nodes for timeline display
  const workflows = useWorkflowStore((s) => s.workflows);
  const workflowNodes = useMemo(() => {
    if (!task.workflowId) return null;
    const wf = workflows.find((w) => w.id === task.workflowId);
    return wf?.nodes || null;
  }, [task.workflowId, workflows]);
  const outputLines = logs?.output_lines || [];

  // Extract PR URL from node results
  const workflowPrUrl = (() => {
    for (const nr of Object.values(nodeResults)) {
      const output = (nr as any)?.output;
      if (typeof output === "string") {
        const match = output.match(/https:\/\/github\.com\/[^/]+\/[^/]+\/pull\/\d+/);
        if (match) return match[0];
      }
    }
    return null;
  })();

  return (
    <div className="space-y-4">
      {/* Job Queue Status */}
      <div>
        <h3 className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
          <Terminal className="w-4 h-4" />
          Job Queue
        </h3>
        {jobs.length === 0 ? (
          <div className="rounded-lg border border-zinc-700/50 bg-zinc-800/30 px-4 py-6 text-center text-sm text-zinc-500">
            No jobs queued yet. Start the task to enqueue a job.
          </div>
        ) : (
          <div className="rounded-lg border border-zinc-700/50 divide-y divide-zinc-700/50 overflow-hidden">
            {jobs.map((job) => (
              <div key={job.id} className="flex items-center gap-3 px-4 py-2.5 bg-zinc-800/20">
                <JobStatusBadge status={job.status} />
                <span className="text-sm text-zinc-300 font-mono">{job.event_type}</span>
                <span className="text-xs text-zinc-500 ml-auto">
                  #{job.id}
                </span>
                <span className="text-xs text-zinc-500">
                  {timeAgo(job.created_at)}
                </span>
                {job.status === "processing" && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-400" />
                )}
              </div>
            ))}
            {/* Show job errors */}
            {jobs.filter(j => j.error).map((job) => (
              <div key={`err-${job.id}`} className="px-4 py-2 bg-red-500/5">
                <div className="text-xs text-red-400 font-mono whitespace-pre-wrap">
                  Job #{job.id}: {job.error}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Workflow Run Details */}
      {workflowRun && (
        <div>
          <h3 className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Workflow Run
            <span className="text-xs text-zinc-500 font-normal">
              {truncateId(workflowRun.id)}
            </span>
            <JobStatusBadge status={workflowRun.status} />
            {workflowRun.current_node_id && workflowRun.status === "running" && (
              <span className="text-xs text-violet-400 flex items-center gap-1 ml-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                {humanizeStepKey(workflowRun.current_node_id)}
              </span>
            )}
          </h3>

          {/* Timing */}
          {workflowRun.started_at && (
            <div className="text-xs text-zinc-500 mb-2 flex items-center gap-4">
              <span>Started: {new Date(workflowRun.started_at).toLocaleTimeString()}</span>
              {workflowRun.completed_at && (
                <span>Completed: {new Date(workflowRun.completed_at).toLocaleTimeString()}</span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {getStepDuration(workflowRun.started_at, workflowRun.completed_at || undefined)}
              </span>
            </div>
          )}

          {/* PR Link */}
          {workflowPrUrl && (
            <div className="flex items-center gap-2 mb-2">
              <a
                href={workflowPrUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1.5 bg-violet-500/10 border border-violet-500/20 rounded-md px-2.5 py-1"
              >
                <GitMerge className="w-3.5 h-3.5" />
                View Pull Request
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}

          {/* Workflow Timeline */}
          {workflowNodes && workflowNodes.length > 0 && (
            <div className="mb-3">
              <WorkflowTimeline
                nodes={workflowNodes}
                nodeResults={nodeResults}
                currentNodeId={workflowRun.current_node_id}
              />
            </div>
          )}

          {/* Review Exhausted Banner */}
          {task.workflowStatus === "paused" && workflowRun?.node_results && (() => {
            const pausedNode = Object.entries(workflowRun.node_results).find(
              ([_, data]) => data.status === "paused" && data.pause_reason === "review_exhausted"
            );
            if (!pausedNode) return null;
            return (
              <ReviewExhaustedBanner
                taskId={task.id}
                nodeResults={workflowRun.node_results}
                reviewNodeId={pausedNode[0]}
              />
            );
          })()}

          {/* Failure Panel with Resume Actions */}
          {workflowRun.status === "failed" && (
            <FailedWorkflowPanel
              workflowRun={workflowRun}
              nodeResults={nodeResults}
              taskId={task.id}
            />
          )}

          {/* Error (non-failed states) */}
          {workflowRun.error && workflowRun.status !== "failed" && (
            <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 mb-3">
              <div className="text-xs font-medium text-red-400 mb-1">Error</div>
              <pre className="text-xs text-red-300 font-mono whitespace-pre-wrap">{workflowRun.error}</pre>
            </div>
          )}

          {/* Node Results */}
          {nodeKeys.length > 0 && (
            <div className="rounded-lg border border-zinc-700/50 overflow-hidden">
              <div className="divide-y divide-zinc-700/50">
                {nodeKeys.map((nodeId) => {
                  const result = nodeResults[nodeId];
                  const nodeStatus = result?.status || "unknown";
                  const output = result?.output || result?.logs || result?.result;
                  const nodeError = result?.error;

                  return (
                    <NodeResultRow
                      key={nodeId}
                      nodeId={nodeId}
                      status={nodeStatus}
                      output={output}
                      error={nodeError}
                      startedAt={result?.started_at}
                      completedAt={result?.completed_at}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* CLI Output Log */}
          {outputLines.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs font-medium text-zinc-400 cursor-pointer hover:text-zinc-300">
                CLI Output ({outputLines.length} lines)
              </summary>
              <div className="mt-2 rounded-lg border border-zinc-700/50 bg-zinc-900/50 max-h-80 overflow-y-auto">
                <pre className="text-xs text-zinc-400 font-mono p-3 whitespace-pre-wrap">
                  {outputLines.map((ol) => ol.line).join("\n")}
                </pre>
              </div>
            </details>
          )}
        </div>
      )}

      {/* Raw metadata log (fallback if no workflow run but has pipeline steps) */}
      {!workflowRun && (task.metadata?.bugfix_steps || task.metadata?.workflow_steps) && (
        <div>
          <h3 className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Pipeline State
          </h3>
          <div className="rounded-lg border border-zinc-700/50 overflow-hidden">
            <OutputBlock text={JSON.stringify(task.metadata?.bugfix_steps || task.metadata?.workflow_steps, null, 2)} />
          </div>
        </div>
      )}

      {/* Empty state */}
      {!workflowRun && jobs.length === 0 && !task.metadata?.bugfix_steps && !task.metadata?.workflow_steps && (
        <div className="rounded-lg border border-zinc-700/50 bg-zinc-800/30 px-4 py-12 text-center">
          <Terminal className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">No activity yet</p>
          <p className="text-xs text-zinc-500 mt-1">
            Start the task to begin the pipeline. Logs will appear here.
          </p>
        </div>
      )}

      <div ref={logEndRef} />
    </div>
  );
}

// ── Copy button ───────────────────────────────────────────────────
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded hover:bg-zinc-700 transition-colors text-zinc-500 hover:text-zinc-300"
      title="Copy to clipboard"
    >
      {copied
        ? <Check className="w-3.5 h-3.5 text-emerald-400" />
        : <Copy className="w-3.5 h-3.5" />
      }
    </button>
  );
}

// ── Collapsible output block ──────────────────────────────────────
const PREVIEW_LINES = 12;
const LONG_THRESHOLD = 600; // chars

function OutputBlock({ text, isError }: { text: string; isError?: boolean }) {
  const formatted = typeof text === "string" ? text : JSON.stringify(text, null, 2);
  const isLong = formatted.length > LONG_THRESHOLD;
  const [showFull, setShowFull] = useState(!isLong);
  const lines = formatted.split("\n");
  const preview = lines.slice(0, PREVIEW_LINES).join("\n");
  const hasMore = isLong && lines.length > PREVIEW_LINES;

  return (
    <div className="relative group">
      <div className="absolute top-1.5 right-1.5 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {hasMore && (
          <button
            onClick={() => setShowFull(!showFull)}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-zinc-700/80 hover:bg-zinc-600 text-zinc-300 transition-colors"
          >
            <ChevronsUpDown className="w-3 h-3" />
            {showFull ? "Collapse" : `Show all (${lines.length} lines)`}
          </button>
        )}
        <CopyButton text={formatted} />
      </div>
      <pre className={cn(
        "text-xs font-mono whitespace-pre-wrap rounded p-2 pr-24",
        isError
          ? "text-red-400 bg-red-500/10"
          : "text-zinc-400 bg-zinc-900",
        showFull ? "max-h-[70vh] overflow-auto" : "max-h-72 overflow-hidden"
      )}>
        {showFull ? formatted : preview}
      </pre>
      {!showFull && hasMore && (
        <button
          onClick={() => setShowFull(true)}
          className="w-full text-center py-1.5 text-[11px] text-zinc-500 hover:text-zinc-300 bg-gradient-to-t from-zinc-900 via-zinc-900/95 to-transparent -mt-8 relative z-[1] rounded-b transition-colors"
        >
          Show {lines.length - PREVIEW_LINES} more lines
        </button>
      )}
    </div>
  );
}

// ── Output size hint ──────────────────────────────────────────────
function outputSizeHint(text?: string): string | null {
  if (!text) return null;
  const len = text.length;
  if (len < 200) return null;
  const lines = text.split("\n").length;
  if (lines > 1) return `${lines} lines`;
  if (len > 1000) return `${Math.round(len / 1000)}k chars`;
  return null;
}

// ── Node result expandable row ────────────────────────────────────
function NodeResultRow({
  nodeId,
  status,
  output,
  error,
  startedAt,
  completedAt,
}: {
  nodeId: string;
  status: string;
  output?: string;
  error?: string;
  startedAt?: string;
  completedAt?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasContent = !!(output || error);
  const duration = getStepDuration(startedAt, completedAt);
  const sizeHint = outputSizeHint(output) || outputSizeHint(error);

  return (
    <div>
      <button
        className={cn(
          "w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-zinc-800/30 transition-colors",
          hasContent && "cursor-pointer"
        )}
        onClick={() => hasContent && setExpanded(!expanded)}
      >
        <StepIcon status={status === "success" ? "completed" : status} />
        <span className="text-sm text-zinc-200 font-mono">{humanizeStepKey(nodeId)}</span>
        {sizeHint && !expanded && (
          <span className="text-[10px] text-zinc-600 font-mono">{sizeHint}</span>
        )}
        {duration && (
          <span className="text-xs text-zinc-500 tabular-nums ml-auto flex-shrink-0">
            {duration}
          </span>
        )}
        {hasContent && (
          expanded
            ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
            : <ChevronRight className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
        )}
      </button>
      {expanded && (
        <div className="px-4 pb-3 pt-1 space-y-2">
          {error && <OutputBlock text={error} isError />}
          {output && <OutputBlock text={output} />}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════
// Main Modal
// ══════════════════════════════════════════════════════════════════

export function TaskDetailModal({ task, isOpen, onClose }: TaskDetailModalProps) {
  const { updateTask, removeTask, retryTaskPipeline, resumeTaskFromFailedNode, startTask, stopTask, mergeTaskPR, fixTaskMergeWithAI } = useWorldStore();
  const workflows = useWorkflowStore((s) => s.workflows);
  const fetchWorkflows = useWorkflowStore((s) => s.fetchWorkflows);
  const { toast } = useToast();
  const { canStartTask, canStopTask, canRetryPipeline, canMergePR } = useWorkspaceFeatures();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [humanReviewRequired, setHumanReviewRequired] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState("");
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("general");
  const [merging, setMerging] = useState(false);
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [canResumeFromNode, setCanResumeFromNode] = useState<string | null>(null);

  const pipelineSteps = useMemo(() => buildPipelineSteps(task?.metadata), [task?.metadata]);
  const totalElapsed = useMemo(() => {
    if (!pipelineSteps.length) return null;
    const starts = pipelineSteps.map((s) => s.started_at).filter(Boolean) as string[];
    const ends = pipelineSteps.map((s) => s.completed_at).filter(Boolean) as string[];
    if (!starts.length) return null;
    const earliest = starts.reduce((a, b) => (a < b ? a : b));
    const latestEnd = ends.length ? ends.reduce((a, b) => (a > b ? a : b)) : undefined;
    const isRunning = task?.workflowStatus === "running";
    return getStepDuration(earliest, isRunning ? undefined : latestEnd);
  }, [pipelineSteps, task?.workflowStatus]);

  const workflowOptions = useMemo(() => {
    return [...workflows].sort((a, b) => {
      const aTs = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bTs = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return bTs - aTs;
    });
  }, [workflows]);
  const selectedWorkflow = useMemo(
    () => workflowOptions.find((workflow) => workflow.id === selectedWorkflowId),
    [workflowOptions, selectedWorkflowId],
  );
  const hasSelectedWorkflowOption = useMemo(
    () => workflowOptions.some((workflow) => workflow.id === selectedWorkflowId),
    [workflowOptions, selectedWorkflowId],
  );

  useEffect(() => {
    if (isOpen) {
      void fetchWorkflows();
    }
  }, [isOpen, fetchWorkflows]);

  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description || "");
      setHumanReviewRequired(task.humanReviewRequired || false);
      setSelectedWorkflowId(task.workflowId || "");
      setExpandedStep(null);
      setActiveTab("general");
    }
  }, [task]);

  useEffect(() => {
    if (!task || task.workflowStatus !== "failed") {
      setCanResumeFromNode(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const Database = (await import("@tauri-apps/plugin-sql")).default;
        const db = await Database.load("sqlite:factory-local.db");
        for (const table of ["workflow_runs", "local_workflow_runs"]) {
          try {
            const rows: any[] = await db.select(
              `SELECT current_node_id FROM ${table} WHERE task_id = ? AND status = 'failed' AND current_node_id IS NOT NULL ORDER BY started_at DESC LIMIT 1`,
              [task.id],
            );
            if (!cancelled && rows.length > 0 && rows[0].current_node_id) {
              setCanResumeFromNode(String(rows[0].current_node_id));
              return;
            }
          } catch { /* table may not exist */ }
        }
        if (!cancelled) setCanResumeFromNode(null);
      } catch {
        if (!cancelled) setCanResumeFromNode(null);
      }
    })();
    return () => { cancelled = true; };
  }, [task?.id, task?.workflowStatus]);

  if (!task) return null;

  const handleSave = async () => {
    const success = await updateTask(task.id, {
      title,
      description,
      humanReviewRequired,
      workflowId: selectedWorkflowId || undefined,
    });
    if (success) {
      toast({ title: "Task updated" });
      onClose();
    } else {
      toast({ title: "Failed to update task", variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    const success = await removeTask(task.id);
    if (success) {
      toast({ title: "Task removed" });
      onClose();
    } else {
      toast({ title: "Failed to remove task", variant: "destructive" });
    }
  };

  const handleRetry = async () => {
    const success = await retryTaskPipeline(task.id);
    if (success) {
      toast({ title: "Pipeline retry queued" });
    } else {
      toast({ title: "Failed to retry pipeline", variant: "destructive" });
    }
  };

  const handleResume = async () => {
    const success = await resumeTaskFromFailedNode(task.id);
    if (success) {
      toast({ title: `Resuming from ${canResumeFromNode}` });
      setCanResumeFromNode(null);
    } else {
      toast({ title: "Failed to resume pipeline", variant: "destructive" });
    }
  };

  const handleStart = async () => {
    if (selectedWorkflowId && selectedWorkflowId !== task.workflowId) {
      const updated = await updateTask(task.id, { workflowId: selectedWorkflowId });
      if (!updated) {
        toast({
          title: "Failed to update workflow",
          description: useWorldStore.getState().saveError || "Could not apply selected workflow before starting.",
          variant: "destructive",
        });
        return;
      }
    }
    const success = await startTask(task.id);
    if (success) {
      toast({ title: "Task started" });
    } else {
      toast({
        title: "Failed to start task",
        description: useWorldStore.getState().saveError || "Failed to start task. Check worker status and try again.",
        variant: "destructive",
      });
    }
  };

  const handleStop = async () => {
    const success = await stopTask(task.id);
    if (success) {
      toast({ title: "Task stopped" });
    } else {
      toast({ title: "Failed to stop task", variant: "destructive" });
    }
  };

  const handleFixMergeConflictWithAI = async () => {
    toast({
      title: "Fixing merge conflict with AI...",
      description: "Resolving branch conflicts and retrying merge.",
    });

    try {
      const fixed = await fixTaskMergeWithAI(task.id);
      if (fixed) {
        toast({ title: "Conflict resolved and PR merged" });
        onClose();
        return;
      }
    } catch (error) {
      const rawMessage = error instanceof Error
        ? error.message
        : (useWorldStore.getState().saveError || "Could not resolve merge conflicts automatically.");
      toast({
        title: "AI merge fix failed",
        description: getMergeErrorMessage(rawMessage),
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "AI merge fix failed",
      description: getMergeErrorMessage(useWorldStore.getState().saveError || "Could not resolve merge conflicts automatically."),
      variant: "destructive",
    });
  };

  const handleMerge = async () => {
    setShowMergeConfirm(false);
    setMerging(true);
    try {
      const success = await mergeTaskPR(task.id);
      if (success) {
        toast({ title: "PR merged successfully" });
        onClose();
      } else {
        const parsedError = parseMergeError(useWorldStore.getState().saveError || "Could not merge the PR. Check GitHub for details.");
        toast({
          title: parsedError.isMergeConflict ? "Merge conflict detected" : "Merge failed",
          description: parsedError.message,
          variant: "destructive",
          action: parsedError.isMergeConflict
            ? (
              <ToastAction altText="Fix merge conflict with AI" onClick={() => { void handleFixMergeConflictWithAI(); }}>
                Fix with AI
              </ToastAction>
            )
            : undefined,
        });
      }
    } catch (error) {
      const rawMessage = error instanceof Error
        ? error.message
        : (useWorldStore.getState().saveError || "Could not merge the PR. Check GitHub for details.");
      const parsedError = parseMergeError(rawMessage);
      toast({
        title: parsedError.isMergeConflict ? "Merge conflict detected" : "Merge failed",
        description: parsedError.message,
        variant: "destructive",
        action: (
          <ToastAction altText="Fix merge conflict with AI" onClick={() => { void handleFixMergeConflictWithAI(); }}>
            Fix with AI
          </ToastAction>
        ),
      });
    } finally {
      setMerging(false);
    }
  };

  const category = issueTypeToCategory[task.issueType] || "Task";
  const hasPipeline = task.workflowStatus != null;
  const isPipelineActive = task.workflowStatus === "running" || task.workflowStatus === "queued";
  const canStart = canStartTask
    && (task.pipelineStage === "backlog" || task.pipelineStage === "planning")
    && !isPipelineActive;
  const mergeableStages = ["human_review", "ai_review", "in_progress", "done"];
  const canMerge = canMergePR
    && (mergeableStages.includes(task.pipelineStage) || task.workflowStatus === "completed")
    && task.pipelineStage !== "backlog";
  const prUrl = task.metadata?.bugfix_steps?.pr?.artifacts?.pr_url
    ?? task.metadata?.pr_url
    ?? task.metadata?.prUrl
    ?? null;
  const mergeUnavailableReason =
    !canMerge && !canMergePR
      ? "Merging PRs requires a GitHub PAT configured in Git settings."
      : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-[#18181B] border-zinc-700 text-white max-h-[90vh] flex flex-col">
        <ModalErrorBoundary>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>Task Details</span>
            <span className="text-xs text-zinc-500 font-normal font-mono">
              {truncateId(task.id)}
            </span>
            {isPipelineActive && (
              <span className="text-xs text-violet-400 flex items-center gap-1 font-normal">
                <Loader2 className="w-3 h-3 animate-spin" />
                {task.workflowStatus}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {category} &middot; {task.pipelineStage}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="bg-zinc-800/50 border border-zinc-700/50 w-fit">
            <TabsTrigger value="general" className="gap-1.5 data-[state=active]:bg-zinc-700">
              <FileText className="w-3.5 h-3.5" />
              General
            </TabsTrigger>
            <TabsTrigger value="logs" className="gap-1.5 data-[state=active]:bg-zinc-700">
              <Terminal className="w-3.5 h-3.5" />
              Logs
              {isPipelineActive && (
                <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── General Tab ─────────────────────────────────────── */}
          <TabsContent value="general" className="flex-1 overflow-y-auto pr-1" style={{ maxHeight: "calc(90vh - 220px)" }}>
            <div className="space-y-6 py-2">
              <div className="space-y-2">
                <Label htmlFor="title">Task Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Describe the feature, bug fix, or improvement..."
                />
              </div>

              <div className="space-y-2">
                <Label>Workflow</Label>
                <Select
                  value={selectedWorkflowId}
                  onValueChange={setSelectedWorkflowId}
                  disabled={isPipelineActive}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Select workflow..." />
                  </SelectTrigger>
                  <SelectContent>
                    {!hasSelectedWorkflowOption && selectedWorkflowId && (
                      <SelectItem value={selectedWorkflowId}>
                        Missing workflow ({truncateId(selectedWorkflowId)})
                      </SelectItem>
                    )}
                    {workflowOptions.map((workflow) => (
                      <SelectItem key={workflow.id} value={workflow.id}>
                        {workflow.name} ({workflow.issueType})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500">
                  Used when starting the task. Current:{" "}
                  {selectedWorkflow
                    ? `${selectedWorkflow.name} (${truncateId(selectedWorkflow.id)})`
                    : selectedWorkflowId
                    ? `unknown (${truncateId(selectedWorkflowId)})`
                    : "none"}
                </p>
              </div>

              {/* Pipeline Progress Panel */}
              {hasPipeline && (
                <div className="rounded-lg border border-zinc-700 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-zinc-800/50">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Pipeline Progress</span>
                      {task.workflowStatus === "running" && (
                        <span className="text-xs text-violet-400 flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Running
                        </span>
                      )}
                      {task.workflowStatus === "completed" && (
                        <span className="text-xs text-emerald-400 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Completed
                        </span>
                      )}
                      {task.workflowStatus === "failed" && (
                        <span className="text-xs text-red-400 flex items-center gap-1">
                          <XCircle className="w-3 h-3" />
                          Failed
                        </span>
                      )}
                      {task.workflowStatus === "paused" && (
                        <span className="text-xs text-amber-400 flex items-center gap-1">
                          <PauseCircle className="w-3 h-3" />
                          Paused — needs input
                        </span>
                      )}
                      {task.workflowStatus === "queued" && (
                        <span className="text-xs text-zinc-400">Queued</span>
                      )}
                      {totalElapsed && (
                        <span className="text-xs text-zinc-500 flex items-center gap-1 ml-1">
                          <Clock className="w-3 h-3" />
                          {totalElapsed}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {prUrl && (
                        <a
                          href={prUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View PR
                        </a>
                      )}
                      {task.workflowStatus === "failed" && canRetryPipeline && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleRetry}
                          className="h-7 text-xs gap-1 text-zinc-400 hover:text-white"
                        >
                          <RefreshCw className="w-3 h-3" />
                          Retry
                        </Button>
                      )}
                      {task.workflowStatus === "failed" && canResumeFromNode && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleResume}
                          className="h-7 text-xs gap-1 text-emerald-400 hover:text-emerald-300"
                        >
                          <SkipForward className="w-3 h-3" />
                          Resume from {canResumeFromNode}
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="divide-y divide-zinc-700/50">
                    {pipelineSteps.map((step) => {
                      const isExpanded = expandedStep === step.key;
                      const hasExpandable = step.error || step.logs || step.output || step.artifacts;
                      const duration = getStepDuration(step.started_at, step.completed_at);

                      return (
                        <div key={step.key}>
                          <button
                            className={cn(
                              "w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-zinc-800/30 transition-colors",
                              hasExpandable && "cursor-pointer"
                            )}
                            onClick={() => hasExpandable && setExpandedStep(isExpanded ? null : step.key)}
                          >
                            <StepIcon status={step.status} />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm text-zinc-200">{step.label}</span>
                              {step.description && (
                                <span className="ml-2 text-xs text-zinc-500">{step.description}</span>
                              )}
                            </div>
                            {duration && (
                              <span className="text-xs text-zinc-500 tabular-nums flex-shrink-0">
                                {duration}
                              </span>
                            )}
                            {hasExpandable && (
                              isExpanded
                                ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                                : <ChevronRight className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                            )}
                          </button>

                          {isExpanded && (
                            <div className="px-4 pb-3 pt-1 space-y-2">
                              {step.error && <OutputBlock text={step.error} isError />}
                              {step.logs && <OutputBlock text={step.logs} />}
                              {step.output && <OutputBlock text={step.output} />}
                              {step.artifacts && (
                                <div className="space-y-1">
                                  {step.artifacts.affected_files && (
                                    <div className="text-xs text-zinc-400">
                                      <span className="text-zinc-500">Files: </span>
                                      {step.artifacts.affected_files.map((f: string) => (
                                        <code key={f} className="mx-0.5 text-zinc-300">{f}</code>
                                      ))}
                                    </div>
                                  )}
                                  {step.artifacts.pr_url && (
                                    <a
                                      href={step.artifacts.pr_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      {step.artifacts.pr_url}
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between rounded-lg border border-zinc-700 p-4">
                <div>
                  <Label htmlFor="human-review">Require human review before coding</Label>
                  <p className="text-xs text-zinc-400">
                    You'll be prompted to review the spec and plan before coding begins.
                  </p>
                </div>
                <Switch
                  id="human-review"
                  checked={humanReviewRequired}
                  onCheckedChange={setHumanReviewRequired}
                />
              </div>
            </div>
          </TabsContent>

          {/* ── Logs Tab ────────────────────────────────────────── */}
          <TabsContent value="logs" className="flex-1 overflow-y-auto pr-1" style={{ maxHeight: "calc(90vh - 220px)" }}>
            <ModalErrorBoundary>
              <LogsTab task={task} />
            </ModalErrorBoundary>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex justify-between w-full pt-4 border-t border-zinc-700/50">
            <Button variant="ghost" onClick={handleDelete} className="gap-2 text-red-400 hover:text-red-300 hover:bg-red-500/10">
                <Trash2 className="w-4 h-4" />
                Delete
            </Button>
            <div className="flex gap-2">
                {isPipelineActive && canStopTask && (
                  <Button onClick={handleStop} variant="destructive" className="gap-2">
                    <Square className="w-4 h-4" />
                    Stop
                  </Button>
                )}
                {canStart && (
                  <Button onClick={handleStart} className="gap-2 bg-emerald-600 hover:bg-emerald-500">
                    <Play className="w-4 h-4" />
                    Start Task
                  </Button>
                )}
                {canMerge && !showMergeConfirm && (
                  <Button
                    onClick={() => setShowMergeConfirm(true)}
                    disabled={merging}
                    className="gap-2 bg-[#01B19D] hover:bg-[#00a08e] text-white"
                  >
                    {merging
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Merging...</>
                      : <><GitMerge className="w-4 h-4" /> Merge PR</>
                    }
                  </Button>
                )}
                {showMergeConfirm && (
                  <>
                    <span className="text-xs text-zinc-400 self-center">Merge this PR?</span>
                    <Button
                      onClick={handleMerge}
                      className="gap-2 bg-[#01B19D] hover:bg-[#00a08e] text-white"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Yes, merge
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowMergeConfirm(false)}
                    >
                      Cancel
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={onClose} className="text-[var(--colony-text-secondary)]">Cancel</Button>
                <Button onClick={handleSave} className="gap-2 bg-factory-primary-600 hover:bg-factory-primary-700">
                    <Save className="w-4 h-4" />
                    Save
                </Button>
            </div>
        </DialogFooter>
        {mergeUnavailableReason && (
          <p className="mt-2 text-xs text-zinc-400">{mergeUnavailableReason}</p>
        )}
        </ModalErrorBoundary>
      </DialogContent>
    </Dialog>
  );
}
